import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircle, Send, X } from "lucide-react";
import { fetchWithAuth } from "../Context/UserContext";
import API_URL from "../config";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { cn } from "../lib/utils";

const ANON_SESSION_KEY = "procu_anon_chat_id";
const TECH_ERROR_MSG = "Estamos con problemas técnicos. Intenta de nuevo en unos minutos.";

const QUICK_OPTIONS = [
  { id: "info", label: "¿Qué es Procu?" },
  { id: "licitar", label: "Quiero licitar" },
  { id: "proveedor", label: "Quiero ser proveedor" },
  { id: "precios", label: "Precios" },
];

function getAnonSessionId() {
  let anonId = localStorage.getItem(ANON_SESSION_KEY);
  if (!anonId) {
    anonId = crypto.randomUUID();
    localStorage.setItem(ANON_SESSION_KEY, anonId);
  }
  return anonId;
}

async function callWidget(path, options = {}) {
  const isLoggedIn = !!localStorage.getItem("current_user_id");

  if (isLoggedIn) {
    return fetchWithAuth(`${API_URL}/widget${path}`, options);
  }

  return fetch(`${API_URL}/widget${path}`, {
    ...options,
    headers: {
      ...options.headers,
      "X-Anon-Session-Id": getAnonSessionId(),
    },
  });
}

const ChatWidget = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [usedOptionIds, setUsedOptionIds] = useState([]);
  const scrollRef = useRef(null);
  const remainingOptions = QUICK_OPTIONS.filter((option) => !usedOptionIds.includes(option.id));

  useEffect(() => {
    if (!open || historyLoaded) return;

    const loadHistory = async () => {
      try {
        const response = await callWidget("/history");
        if (response && response.ok) {
          const data = await response.json();
          setMessages(
            data.messages.map((m) => ({
              role: m.sender_type === "bot" ? "bot" : "user",
              content: m.content,
            }))
          );
        }
      } catch (error) {
        console.error("Error al cargar el historial del chat:", error);
      } finally {
        setHistoryLoaded(true);
      }
    };

    loadHistory();
  }, [open, historyLoaded]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const sendPayload = async (userBubbleText, payload) => {
    if (loading) return;

    setMessages((prev) => [...prev, { role: "user", content: userBubbleText }]);
    setLoading(true);

    try {
      const response = await callWidget("/message", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response && response.ok) {
        const data = await response.json();
        setMessages((prev) => [
          ...prev,
          { role: "bot", content: data.reply_text, cta: data.cta },
        ]);
      } else {
        setMessages((prev) => [...prev, { role: "bot", content: TECH_ERROR_MSG }]);
      }
    } catch (error) {
      console.error("Error al enviar mensaje al chat:", error);
      setMessages((prev) => [...prev, { role: "bot", content: TECH_ERROR_MSG }]);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = () => {
    const text = input.trim();
    if (!text) return;
    setInput("");
    sendPayload(text, { message: text });
  };

  const handleQuickOption = (option) => {
    setUsedOptionIds((prev) => [...prev, option.id]);
    sendPayload(option.label, { intent: option.id });
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col items-end gap-2">
      {open && (
        <div className="flex h-96 w-80 flex-col overflow-hidden rounded-lg border border-border bg-background shadow-xl">
          <div className="flex items-center justify-between border-b border-border bg-primary px-4 py-3 text-primary-foreground">
            <span className="text-sm font-semibold">Lici · Asistente de Procu</span>
            <button
              onClick={() => setOpen(false)}
              aria-label="Cerrar chat"
              className="rounded-md p-1 hover:bg-primary-foreground/10"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 space-y-2 overflow-y-auto p-3">
            {messages.length === 0 && (
              <p className="text-center text-sm text-muted-foreground">
                ¡Hola! Soy Lici, el asistente de Procu. ¿En qué te ayudo?
              </p>
            )}
            {messages.map((message, index) => (
              <div
                key={index}
                className={cn(
                  "flex flex-col gap-1",
                  message.role === "user" ? "items-end" : "items-start"
                )}
              >
                <div
                  className={cn(
                    "max-w-[85%] rounded-lg px-3 py-2 text-sm",
                    message.role === "user"
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-foreground"
                  )}
                >
                  {message.content}
                </div>
                {message.cta && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigate(message.cta.path)}
                  >
                    {message.cta.label}
                  </Button>
                )}
              </div>
            ))}
            {loading && (
              <div className="max-w-[85%] rounded-lg bg-muted px-3 py-2 text-sm text-muted-foreground">
                Escribiendo...
              </div>
            )}
            {!loading && remainingOptions.length > 0 && (
              <div className="flex flex-wrap justify-center gap-2 pt-1">
                {remainingOptions.map((option) => (
                  <Button
                    key={option.id}
                    variant="outline"
                    size="sm"
                    onClick={() => handleQuickOption(option)}
                    disabled={loading}
                  >
                    {option.label}
                  </Button>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 border-t border-border p-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Escribe tu mensaje..."
              disabled={loading}
            />
            <Button size="icon" onClick={handleSend} disabled={loading} aria-label="Enviar mensaje">
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <Button
        size="icon"
        className="h-14 w-14 rounded-full shadow-lg"
        onClick={() => setOpen((prev) => !prev)}
        aria-label={open ? "Cerrar chat con Lici" : "Abrir chat con Lici"}
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </Button>
    </div>
  );
};

export default ChatWidget;
